import { currentUser } from './auth';

/**
 * Advisory client-side mirror of the server's RBAC policy.
 *
 * The server stays the sole authority: the (role × module) policy and
 * the (role × device_class) ceiling are enforced on every protected
 * call. This module only mirrors the *resolved* answer the server already sent
 * on /api/me, so the SPA can hide or redirect views a user can't reach rather
 * than show dead ends. It never grants access — a bypassed guard still meets a
 * server 403/404.
 *
 * The capability set is the composed (role × module) ∩ (role × device_class)
 * set for the session's bound device class, so a phone session already reflects
 * the phone ceiling. It is auth-bound — fixed for the session — not
 * viewport-bound: resizing the window changes layout, never capabilities (a
 * device-class switch forces re-auth, ).
 */

export type Module =
  | 'account'
  | 'catalog'
  | 'orders'
  | 'customers'
  | 'console'
  | 'compliance'
  | 'admin';

export type Capability = 'view' | 'create' | 'edit' | 'approve' | 'export';

// Which module a client route belongs to. Only these routes are
// capability-gated; everything else — home, the Me hub, account settings, the
// public auth pages — is reachable by any signed-in user and is intentionally
// absent. Matching is prefix-aware so sub-pages (e.g. /orders/:id, added in a
// later milestone) inherit their parent's gate.
const MODULE_FOR_PATH: Record<string, Module> = {
  '/account': 'account',
  '/catalog': 'catalog',
  '/orders': 'orders',
  '/customers': 'customers',
  '/console': 'console',
  '/compliance': 'compliance',
  '/admin': 'admin',
};

/**
 * can reports whether the signed-in user holds `cap` on `module`.
 *
 * Fail-open when the capability set is absent: an older or partial /api/me
 * payload shouldn't hide a feature the user may well have — the server still
 * enforces it. Once /api/me carries `capabilities` (always, post-) the
 * mirror is authoritative for UX, and a missing or empty module entry means
 * "hide".
 */
export function can(module: Module, cap: Capability): boolean {
  const caps = currentUser()?.capabilities;
  if (!caps) return true; // mirror data unavailable — don't hide; server enforces
  return caps[module]?.includes(cap) ?? false;
}

// canViewModule is the gate for *reaching* a module surface (nav + route
// guard): view is the floor of every non-empty cell, so it answers "should this
// module be visible at all".
export function canViewModule(module: Module): boolean {
  return can(module, 'view');
}

// moduleForPath returns the module a route is gated by, or null when the route
// is open to any signed-in user. Prefix-aware so a module's sub-pages share its
// gate.
export function moduleForPath(path: string): Module | null {
  for (const prefix in MODULE_FOR_PATH) {
    if (path === prefix || path.startsWith(prefix + '/')) return MODULE_FOR_PATH[prefix];
  }
  return null;
}

// canViewPath answers whether the signed-in user may reach `path`. Open routes
// (not in the gate map) are always allowed; gated routes require view on their
// module.
export function canViewPath(path: string): boolean {
  const module = moduleForPath(path);
  return module === null ? true : canViewModule(module);
}

/**
 * Desktop-pin policy — the one piece of the server's authorization the composed
 * capability mirror can't express. On top of the (role × module) ∩ (role ×
 * device) set, the server adds a device "key gate" to a few surfaces
 * (rbac.RequireDesktop / RequireDesktopLookup): they work only from a desktop, a
 * pin `view` survives. Without this, a phone/tablet staff session reads `view`
 * on those modules, walks in, and dead-ends on a server 403 DESKTOP_REQUIRED
 * its generic catch can only turn into "try again".
 *
 * The pins (kept in lockstep with pkg/api/router.go):
 *   - console    — desktop-only for ALL staff, admin included (RequireDesktop,
 *                  no admin exemption). Covers every console lens.
 *   - compliance — desktop-only (admin-only surface; RequireDesktop).
 *   - customers  — desktop-only for non-admin; admins look up from any device
 *                  (RequireDesktopLookup's matrix-driven admin exemption).
 *
 * Advisory only, like the rest of this mirror: the server re-enforces the pin on
 * every call, so this never grants access — it only avoids the dead end. It
 * fails open when the device class is unknown (an older /api/me omits it): we
 * don't hide a surface we can't prove is pinned, and the server plus the
 * page-level DESKTOP_REQUIRED state still catch it.
 */
const DESKTOP_PINNED: Partial<Record<Module, 'all' | 'non-admin'>> = {
  console: 'all',
  compliance: 'all',
  customers: 'non-admin',
};

// requiresDesktop reports whether the signed-in user's current (bound) device
// class is barred from `module` by the desktop pin — true only off desktop, and
// for `customers` only when the user is not an admin.
export function requiresDesktop(module: Module): boolean {
  const pin = DESKTOP_PINNED[module];
  if (!pin) return false;
  const u = currentUser();
  if (!u) return false;
  // Unknown or desktop class → never pinned (fail-open on unknown).
  if (u.deviceClass === undefined || u.deviceClass === 'desktop') return false;
  if (pin === 'non-admin' && u.role === 'admin') return false;
  return true;
}

// requiresDesktopPath is the route-guard form: it maps a path to its module and
// reports whether the desktop pin bars this session from reaching it. Open
// routes (no module) are never pinned.
export function requiresDesktopPath(path: string): boolean {
  const module = moduleForPath(path);
  return module === null ? false : requiresDesktop(module);
}
